import type { LucideIcon } from 'lucide-react';
import {
  Receipt,
  Pencil,
  Trash2,
  ArrowLeftRight,
  MessageSquare,
  UserPlus,
  UserMinus,
  ShieldCheck,
  TriangleAlert,
  CheckCircle,
  XCircle,
  Undo2,
  RefreshCcw,
  Activity,
  Settings,
  Upload,
} from 'lucide-react';
import type { ActivityRow, ActivityParams } from '@/api/activities';
import type { NavigateFunction } from 'react-router';
import { formatMinorAmount, getMinorUnitsSync } from '@sharecost/shared';
import i18next from '@/i18n';

// --- Formatted output type ---

export interface FormattedActivity {
  id: string;
  activityType: string;
  title: string;
  subtitle?: string;
  amountMinor?: number;
  currency?: string;
  groupId?: string;
  groupName?: string;
  groupColor?: string;
  entityId: string;
  entityType: string;
  params: ActivityParams;
  timestamp: string;
}

// --- Main transform ---

export function formatActivity(row: ActivityRow, currentUserId: string): FormattedActivity {
  const { title, subtitle } = formatActivityText(row, currentUserId);
  const params = row.params;
  const groupId = row.group_id && row.group_id !== '' ? row.group_id : undefined;

  return {
    id: row.id,
    activityType: row.activity_type,
    title,
    subtitle,
    amountMinor: params.amount,
    currency: params.currency,
    groupId,
    groupName: params.group_name,
    groupColor: row.group_color,
    entityId: row.entity_id,
    entityType: row.entity_type,
    params,
    timestamp: row.created_at,
  };
}

// --- Text formatting (ported from mobile activity-new.rxdb.service.ts) ---

export function formatActivityText(
  row: ActivityRow,
  currentUserId: string,
): { title: string; subtitle?: string } {
  const params = row.params;
  const isSelf = row.actor_id === currentUserId;
  // Same keys as the mobile activity feed (activity-new.rxdb.service.ts)
  const t = i18next.getFixedT(null, 'home');
  const actor = params.actor_name || t('activity.someone');
  const expenseDescription = params.expense_description || params.description;

  switch (row.activity_type) {
    // --- Expenses ---
    case 'expense.created':
      return {
        title: isSelf ? t('activity.youAddedExpense') : t('activity.someoneAddedExpense', { name: actor }),
        subtitle: params.description,
      };

    case 'expense.updated': {
      const changes: string[] = [];

      if (params.changes?.amount && params.changes.amount.old !== params.changes.amount.new) {
        const oldCurrency = params.changes.amount.old_currency || params.currency || '';
        const newCurrency = params.changes.amount.new_currency || params.currency || '';
        const oldFormatted = formatMinorAmount(params.changes.amount.old, oldCurrency, getMinorUnitsSync(oldCurrency));
        const newFormatted = formatMinorAmount(params.changes.amount.new, newCurrency, getMinorUnitsSync(newCurrency));
        changes.push(t('changeDetail.amount', { old: oldFormatted, new: newFormatted }));
      }
      if (params.changes?.description) {
        changes.push(t('changeDetail.description', { old: params.changes.description.old, new: params.changes.description.new }));
      }
      if (params.changes?.expense_date) {
        const oldDate = new Date(params.changes.expense_date.old).toLocaleDateString();
        const newDate = new Date(params.changes.expense_date.new).toLocaleDateString();
        changes.push(t('changeDetail.date', { old: oldDate, new: newDate }));
      }
      if (params.changes?.category) changes.push(i18next.t('web:activityFormatter.categoryChanged'));
      if (params.changes?.receipt) {
        if (params.changes.receipt.added) changes.push(t('activity.receiptAdded'));
        else if (params.changes.receipt.removed) changes.push(t('activity.receiptRemoved'));
        else if (params.changes.receipt.updated) changes.push(t('activity.receiptUpdated'));
      }
      if (params.changes?.notes) {
        const hasOld = params.changes.notes.old && params.changes.notes.old.length > 0;
        const hasNew = params.changes.notes.new && params.changes.notes.new.length > 0;
        if (!hasOld && hasNew) changes.push(i18next.t('web:activityFormatter.notesAdded'));
        else if (hasOld && !hasNew) changes.push(i18next.t('web:activityFormatter.notesRemoved'));
        else if (hasOld && hasNew) changes.push(i18next.t('web:activityFormatter.notesUpdated'));
      }

      return {
        title: isSelf
          ? t('activity.youUpdatedExpense', { description: params.description })
          : t('activity.someoneUpdatedExpense', { name: actor, description: params.description }),
        subtitle: changes.length > 0 ? changes.join(' • ') : undefined,
      };
    }

    case 'expense.deleted':
      return {
        title: isSelf
          ? t('activity.youDeletedExpense', { description: params.description })
          : t('activity.someoneDeletedExpense', { name: actor, description: params.description }),
      };

    // --- Comments ---
    case 'comment.added':
      return {
        title: isSelf
          ? t('activity.youCommented', { description: params.expense_description })
          : t('activity.someoneCommented', { name: actor, description: params.expense_description }),
        subtitle: params.comment_preview,
      };

    case 'comment.edited':
      return {
        title: isSelf
          ? t('activity.youEditedComment', { description: params.expense_description })
          : t('activity.someoneEditedComment', { name: actor, description: params.expense_description }),
        subtitle:
          params.old_text && params.comment_preview
            ? `"${params.old_text}" → "${params.comment_preview}"`
            : params.comment_preview,
      };

    case 'comment.deleted':
      return {
        title: isSelf
          ? t('activity.youDeletedComment', { description: params.expense_description })
          : t('activity.someoneDeletedComment', { name: actor, description: params.expense_description }),
      };

    // --- Settlements ---
    case 'settlement.created': {
      const isFrom = params.from_user_id === currentUserId;
      const isTo = params.to_user_id === currentUserId;
      const fromName = params.from_user_name || t('activity.someone');
      const toName = params.to_user_name || t('activity.someone');
      let title: string;
      if (isFrom) title = t('activity.youPaid', { name: toName });
      else if (isTo) title = t('activity.someonePaidYou', { name: fromName });
      else title = t('activity.someonePaidSomeone', { from: fromName, to: toName });
      return {
        title,
        subtitle: !params.description || params.description === 'Settlement' ? t('activity.settlement') : params.description,
      };
    }

    case 'settlement.updated': {
      const isFrom = params.from_user_id === currentUserId;
      const isTo = params.to_user_id === currentUserId;
      const fromName = params.from_user_name || t('activity.someone');
      const toName = params.to_user_name || t('activity.someone');
      let title: string;
      if (isFrom) title = t('activity.youUpdatedSettlement', { name: toName });
      else if (isTo) title = t('activity.someoneUpdatedSettlementYou', { name: fromName });
      else title = t('activity.someoneUpdatedSettlement', { from: fromName, to: toName });
      return { title };
    }

    case 'settlement.deleted':
      return {
        title: isSelf ? t('activity.youDeletedSettlement') : t('activity.someoneDeletedSettlement', { name: actor }),
      };

    // --- Members ---
    case 'group.member.added': {
      const isNew = params.member_id === currentUserId;
      const group = params.group_name || t('activity.theGroup');
      return {
        title: isNew
          ? t('activity.youJoined', { group })
          : t('activity.someoneJoined', { name: params.member_name || t('activity.someone'), group }),
      };
    }

    case 'group.member.removed': {
      const isRemoved = params.member_id === currentUserId;
      const group = params.group_name || t('activity.theGroup');
      return {
        title: isRemoved
          ? t('activity.youLeft', { group })
          : t('activity.someoneLeft', { name: params.member_name || t('activity.someone'), group }),
      };
    }

    case 'group.member.role.changed': {
      const isMember = params.member_id === currentUserId;
      const promoted = params.new_role === 'admin';
      if (isMember) {
        return {
          title: promoted ? t('activity.youPromotedAdmin') : t('activity.youDemotedMember'),
          subtitle: params.group_name,
        };
      }
      const name = params.member_name || t('activity.someone');
      return {
        title: promoted ? t('activity.someonePromotedAdmin', { name }) : t('activity.someoneDemotedMember', { name }),
        subtitle: params.group_name,
      };
    }

    // --- Disputes ---
    case 'dispute.raised':
      return {
        title: isSelf
          ? t('activity.youRaisedDispute', { description: expenseDescription })
          : t('activity.someoneRaisedDispute', { name: actor, description: expenseDescription }),
        subtitle: params.reason_text || params.group_name,
      };

    case 'dispute.resolved':
      return {
        title: isSelf
          ? t('activity.youResolvedDispute', { description: expenseDescription })
          : t('activity.someoneResolvedDispute', { name: actor, description: expenseDescription }),
        subtitle: params.resolution_note || t('activity.expenseWasEdited'),
      };

    case 'dispute.rejected':
      return {
        title: isSelf
          ? t('activity.youRejectedDispute', { description: expenseDescription })
          : t('activity.someoneRejectedDispute', { name: actor, description: expenseDescription }),
        subtitle: params.resolution_note || params.group_name,
      };

    case 'dispute.withdrawn':
      return {
        title: isSelf
          ? t('activity.youWithdrewDispute', { description: expenseDescription })
          : t('activity.someoneWithdrewDispute', { name: actor, description: expenseDescription }),
      };

    case 'dispute.reopened':
      return {
        title: isSelf
          ? t('activity.youReopenedDispute', { description: expenseDescription })
          : t('activity.someoneReopenedDispute', { name: actor, description: expenseDescription }),
        subtitle: params.reason_text || params.group_name,
      };

    // --- Import ---
    case 'import.completed':
      return {
        title: isSelf
          ? t('activity.youImported', { count: params.count ?? '' })
          : t('activity.someoneImported', { name: actor, count: params.count ?? '' }),
        subtitle: params.group_name,
      };

    // --- Group ---
    case 'group.updated': {
      const groupChanges = params.group_changes || [];
      const groupCurrency = params.currency || '';

      const changeLabels: Record<string, string> = {
        name: t('activity.groupFieldName'),
        description: t('activity.groupFieldDescription'),
        currency_code: t('activity.groupFieldCurrency'),
        color: t('activity.groupFieldColor'),
        budget_enabled: t('activity.groupFieldBudget'),
        budget_amount_minor: t('activity.groupFieldBudget'),
        budget_period: t('activity.groupFieldBudgetPeriod'),
        budget_alert_threshold: t('activity.groupFieldAlertThreshold'),
        budget_start_day: i18next.t('web:activityFormatter.budgetMonthStart'),
      };

      const formatChangeValue = (field: string, value: string | number | boolean | null | undefined): string => {
        if (value === null || value === undefined) return t('changeDetail.none');
        if (typeof value === 'boolean') return value ? t('changeDetail.on') : t('changeDetail.off');
        if (field === 'budget_alert_threshold') return `${value}%`;
        if (field === 'budget_amount_minor' && typeof value === 'number') {
          const minorUnits = groupCurrency ? getMinorUnitsSync(groupCurrency) : 2;
          return formatMinorAmount(value, groupCurrency || '', minorUnits);
        }
        return String(value);
      };

      const changeDescriptions = groupChanges.map((c) => {
        const label = changeLabels[c.field] || c.field;
        const oldVal = formatChangeValue(c.field, c.old_value);
        const newVal = formatChangeValue(c.field, c.new_value);
        return `${label}: ${oldVal} → ${newVal}`;
      });

      return {
        title: isSelf
          ? t('activity.youUpdatedGroup', { group: params.group_name })
          : t('activity.someoneUpdatedGroup', { name: actor, group: params.group_name }),
        subtitle: changeDescriptions.length > 0 ? changeDescriptions.join(' • ') : undefined,
      };
    }

    default:
      return {
        title: t('activity.someonePerformed', { name: actor, action: row.action }),
        subtitle: row.entity_type,
      };
  }
}

// --- Icon config ---

interface IconConfig {
  Icon: LucideIcon;
  bgClass: string;
  iconClass: string;
}

const ICON_MAP: Record<string, IconConfig> = {
  'expense.created':  { Icon: Receipt,          bgClass: 'bg-indigo-500/15', iconClass: 'text-indigo-600 dark:text-indigo-400' },
  'expense.updated':  { Icon: Pencil,           bgClass: 'bg-amber-500/15',  iconClass: 'text-amber-600 dark:text-amber-400' },
  'expense.deleted':  { Icon: Trash2,           bgClass: 'bg-red-500/15',    iconClass: 'text-red-600 dark:text-red-400' },

  'settlement.created': { Icon: ArrowLeftRight, bgClass: 'bg-green-500/15',  iconClass: 'text-green-600 dark:text-green-400' },
  'settlement.updated': { Icon: ArrowLeftRight, bgClass: 'bg-green-500/15',  iconClass: 'text-green-600 dark:text-green-400' },
  'settlement.deleted': { Icon: Trash2,         bgClass: 'bg-red-500/15',    iconClass: 'text-red-600 dark:text-red-400' },

  'comment.added':    { Icon: MessageSquare,    bgClass: 'bg-sky-500/15',    iconClass: 'text-sky-600 dark:text-sky-400' },
  'comment.edited':   { Icon: MessageSquare,    bgClass: 'bg-sky-500/15',    iconClass: 'text-sky-600 dark:text-sky-400' },
  'comment.deleted':  { Icon: Trash2,           bgClass: 'bg-red-500/15',    iconClass: 'text-red-600 dark:text-red-400' },

  'group.member.added':         { Icon: UserPlus,      bgClass: 'bg-emerald-500/15', iconClass: 'text-emerald-600 dark:text-emerald-400' },
  'group.member.removed':       { Icon: UserMinus,     bgClass: 'bg-orange-500/15',  iconClass: 'text-orange-600 dark:text-orange-400' },
  'group.member.role.changed':  { Icon: ShieldCheck,   bgClass: 'bg-purple-500/15',  iconClass: 'text-purple-600 dark:text-purple-400' },

  'group.updated':              { Icon: Settings,     bgClass: 'bg-slate-500/15',   iconClass: 'text-slate-600 dark:text-slate-400' },

  'import.completed':           { Icon: Upload,       bgClass: 'bg-indigo-500/15',  iconClass: 'text-indigo-600 dark:text-indigo-400' },

  'dispute.raised':    { Icon: TriangleAlert, bgClass: 'bg-yellow-500/15', iconClass: 'text-yellow-600 dark:text-yellow-400' },
  'dispute.resolved':  { Icon: CheckCircle,   bgClass: 'bg-green-500/15',  iconClass: 'text-green-600 dark:text-green-400' },
  'dispute.rejected':  { Icon: XCircle,       bgClass: 'bg-red-500/15',    iconClass: 'text-red-600 dark:text-red-400' },
  'dispute.withdrawn': { Icon: Undo2,         bgClass: 'bg-surface-tertiary', iconClass: 'text-text-tertiary' },
  'dispute.reopened':  { Icon: RefreshCcw,    bgClass: 'bg-yellow-500/15', iconClass: 'text-yellow-600 dark:text-yellow-400' },
};

const DEFAULT_ICON: IconConfig = { Icon: Activity, bgClass: 'bg-surface-tertiary', iconClass: 'text-text-tertiary' };

export function getActivityIconConfig(activityType: string): IconConfig {
  return ICON_MAP[activityType] ?? DEFAULT_ICON;
}

// --- Navigation helper ---

export function navigateToActivity(activity: FormattedActivity, navigate: NavigateFunction) {
  const { entityType, entityId, params, groupId } = activity;

  switch (entityType) {
    case 'expense':
      navigate(`/expenses/${entityId}`);
      break;
    case 'comment':
    case 'expense_comment':
    case 'dispute':
      // Comments and disputes link to their parent expense
      if (params.expense_id) {
        navigate(`/expenses/${params.expense_id}`);
      } else if (groupId) {
        navigate(`/groups/${groupId}`);
      }
      break;
    case 'settlement':
    case 'group_member':
    case 'group':
      if (groupId) {
        navigate(`/groups/${groupId}`);
      }
      break;
  }
}
